import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Table,
  DatePicker,
  Select,
  TablePaginationConfig,
} from "antd";
import type { FormInstance } from "antd/es/form";
import Text from "antd/es/typography/Text";
import dayjs from "dayjs";
import moment from "moment";
import { EditOutlined } from "@ant-design/icons";

const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();

  return (
    <Form form={form} component={false} data-index={index}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: any;
  required: boolean;
  record: any;
  datatype: string;
  options: any[];
  inputType: string;
  handleSave: (record: any) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  required,
  record,
  datatype,
  options,
  inputType,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<any>(null);
  const form = useContext(EditableContext)!;

  useEffect(() => {
    if (editing && datatype !== "datetime") {
      inputRef.current!.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      let values;
      if (datatype !== "datetime") {
        values = await form.validateFields();
      } else {
        let recoveryDate = await form.validateFields();
        if (typeof recoveryDate[dataIndex] === "string") {
          values = recoveryDate;
        } else {
          values = {
            [dataIndex]: recoveryDate?.earlyDueDate?.$d
              ? moment(recoveryDate.earlyDueDate.$d).format("MM/DD/YYYY")
              : moment(recoveryDate[dataIndex]?.$d ?? undefined).format(
                  "MM/DD/YYYY",
                ),
          };
        }
      }
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.error("Save failed:", errInfo);
    }
  };

  let childNode = children;
  if (editable) {
    childNode =
      editing || datatype === "datetime" ? (
        <Form.Item
          style={{ margin: 0, flex: 1 }}
          name={dataIndex}
          initialValue={
            datatype !== "datetime" ? undefined : dayjs(record[dataIndex])
          }
          getValueProps={(i) => ({
            value: datatype !== "datetime" ? i : dayjs(record[dataIndex]),
          })}
          rules={[
            {
              required: required ?? false,
              message: `${title} is required.`,
            },
          ]}
        >
          {datatype !== "datetime" ? (
            options === undefined ? (
              <Input
                style={{ backgroundColor: "transparent" }}
                ref={inputRef}
                onPressEnter={save}
                onBlur={save}
                type={inputType === "Number" ? "number" : undefined}
                min={inputType === "Number" ? 0 : undefined}
                defaultValue={inputType === "Number" ? 0 : undefined}
              />
            ) : (
              <Select
                ref={inputRef}
                onChange={save}
                options={options}
                onBlur={save}
                style={{ minWidth: "5rem" }}
              />
            )
          ) : (
            <DatePicker
              format={"MM/DD/YYYY"}
              allowClear={false}
              onChange={save}
              value={dayjs(record[dataIndex])}
            />
          )}
        </Form.Item>
      ) : (
        <div
          className="editable-cell-value-wrap"
          style={{ paddingRight: 24, display: "flex", gap: "1rem" }}
          onClick={toggleEdit}
        >
          {children}
          <div>
            <EditOutlined className="action" />
          </div>
        </div>
      );
  }

  return (
    <td
      {...restProps}
      style={{
        minWidth:
          datatype === "datetime" || datatype === "select" ? "8rem" : undefined,
        maxWidth: "8rem",
        overflow: "auto",
      }}
    >
      {childNode}
    </td>
  );
};

type EditableTableProps = Parameters<typeof Table>[0];
type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;
interface CBEditableTableTypes {
  data: any[];
  columns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
    required?: boolean;
    deleted?: boolean;
    backGround?: string;
    datatype?: string;
    options?: any[];
    inputType?: string;
  })[];
  rowsPeerPage?: number;
  addButton?: boolean;
  onChange?: any;
  handleReset?: boolean;
  pagination?: any;
  tableErrors?: string[];
  paginationOnChange?: (props: TablePaginationConfig) => void;
}

const CBEditableTable: React.FC<CBEditableTableTypes> = ({
  data,
  columns,
  addButton,
  onChange,
  handleReset,
  pagination,
  tableErrors,
  paginationOnChange,
}) => {
  const [dataContext, setDataContext] = useState<any[]>(
    data?.map((d, i) => ({ ...d, key: i })) ?? [],
  );
  const [dataSource, setDataSource] = useState<any[]>([...dataContext]);
  const [count, setCount] = useState(2);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const handleAddSource = () => {
    let row: any = { key: String(dataSource.length) };
    columns.map((c) => {
      row[c.dataIndex] = "";
    });

    setDataSource([...dataSource, row]);
    setCount(count + 1);
  };

  useEffect(() => {
    setHasErrors(!!tableErrors?.length);
  }, [tableErrors]);

  const validateAndFormatDatePropertyWithDayjs = (arr: any, keys: any) => {
    return arr.map((obj: any) => {
      for (let key of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];

          let formattedDate = dayjs(value, "MM/DD/YYYY", true);
          if (!formattedDate.isValid()) {
            formattedDate = dayjs(value);
            if (!formattedDate.isValid())
              return { error: `Invalid date format for ${key}` };
          }

          obj[key] = formattedDate.format("MM/DD/YYYY");
        } else return { error: `Property ${key} not found in the object` };
      }
      return obj;
    });
  };

  const handleSave = (row: any) => {
    try {
      const newData = [...dataSource];
      const index = newData.findIndex((item) => row.key === item.key);
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        ...row,
      });

      let cols = columns
        .filter((c) => c.datatype === "datetime")
        .map((c) => c.dataIndex);
      const transformData = cols.length
        ? validateAndFormatDatePropertyWithDayjs(newData, cols)
        : newData;

      setDataSource(transformData);
      onChange(transformData);
    } catch (err) {
      console.error("ERR: ", err);
    }
  };

  const handleDeleteSource = (key: React.Key) => {
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
    onChange(newData);
  };

  const handleResetSource = useCallback(() => {
    setDataSource(dataContext);
  }, [dataContext]);

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnsSource = columns.map((col, colIndex) => {
    const columnWithKey = {
      ...col,
      key: col.key ?? col.dataIndex ?? `col-${colIndex}`,
    };
    if (col.deleted) {
      return {
        ...columnWithKey,
        render: (_: any, record: { key: React.Key }) =>
          dataSource.length >= 1 ? (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDeleteSource(record.key)}
            >
              <a>Delete</a>
            </Popconfirm>
          ) : null,
      };
    }

    if (!col.editable) {
      return {
        ...columnWithKey,
        render(text: any) {
          return {
            props: {
              style: { background: col?.backGround ?? "" },
            },
            children: <div>{text}</div>,
          };
        },
      };
    }
    return {
      ...columnWithKey,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        required: col.required,
        title: col.title,
        datatype: col.datatype,
        options: col.options,
        inputType: col.inputType,
        handleSave,
      }),
      render(text: any) {
        return {
          props: {
            style: { background: col?.backGround ?? "" },
          },
          children: <div>{text}</div>,
        };
      },
    };
  });

  useEffect(() => {
    if (handleReset) handleResetSource();
  }, [handleReset, handleResetSource]);

  useEffect(() => {
    setDataContext(data?.map((d, i) => ({ ...d, key: i })) ?? []);
  }, [data]);

  return (
    <div>
      {addButton ? (
        <Button
          type="primary"
          style={{ marginBottom: 16 }}
          onClick={() => handleAddSource()}
        >
          Add a row
        </Button>
      ) : null}
      <Table
        components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        pagination={pagination}
        onChange={paginationOnChange}
        columns={columnsSource as ColumnTypes}
      />
      {hasErrors && (
        <Text type="danger">
          Table has required fields: {tableErrors?.join(", ")}
        </Text>
      )}
    </div>
  );
};

export default CBEditableTable;
